import React from 'react';
import { Text, TouchableOpacity, View, StyleSheet, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useDispatch, useSelector } from 'react-redux';
import { setCartAssignmentDetailsPrompt } from '../../actions/scanToCartActions';
import I18n from 'i18n-js';

const CartAssignmentDetails = () => {
  const dispatch = useDispatch();

  const { 
    showCartAssignmentDetailsPrompt,
    cartAssignmentDetails 
  } = useSelector(state => state.scanToCart);

  // Don't render if not visible
  if (!showCartAssignmentDetailsPrompt) return null;

  const { notAssignedOrders = [], assignedOrders = [] } = cartAssignmentDetails || {};

  const issueData = notAssignedOrders;
  const successData = assignedOrders;

  const hasIssues = issueData && issueData.length > 0;

  const handleClose = () => {
    dispatch(setCartAssignmentDetailsPrompt(false));
  };

  const renderIssueSection = () => {
    if (!hasIssues) {
      return (
        <View style={styles.successIndicator}>
          <View style={styles.checkIcon}>
            <Text style={styles.checkMark}>✓</Text>
          </View>
          <Text style={styles.noIssuesText}>
            {I18n.t('cartAssignment.issueDetails.noIssues')}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.issueSection}>
        <Text style={styles.issueTitle}>
          {I18n.t('cartAssignment.issueDetails.title')}
        </Text>
        <ScrollView style={styles.issueScrollView} showsVerticalScrollIndicator={true}>
          {issueData.map((issue, index) => (
            <Text key={index} style={styles.issueText}>
              {issue}
            </Text>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderSuccessSection = () => {
    if (successData.length === 0) return null;

    return (
      <View style={styles.successSection}>
        <Text style={styles.successTitle}>
          {I18n.t('cartAssignment.issueDetails.successTitle')}
        </Text>
        <ScrollView style={styles.successScrollView} showsVerticalScrollIndicator={true}>
          {successData.map((assignment, index) => (
            <Text key={index} style={styles.successText}>
              {assignment}
            </Text>
          ))}
        </ScrollView>
      </View>
    );
  };

  return (
    <View style={styles.overlay}>
      <LinearGradient
        locations={[0, 1]}
        start={{ x: 0, y: 1 }}
        end={{ x: 0, y: 0 }}
        colors={['#d47d02', '#ffb636']}
        style={styles.modalContainer}
      >
        <View style={styles.header}>
          <Text style={styles.title}>
            {I18n.t('cartAssignment.issueDetails.title')}
          </Text>
          <TouchableOpacity 
            testID="closeModalButton" 
            onPress={handleClose}
            style={styles.closeButton}
          >
            <Text style={styles.closeIcon}>✕</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.contentContainer}>
          {renderIssueSection()}
          {renderSuccessSection()}
        </View>

        <View style={styles.footerContainer}>
          <TouchableOpacity 
            testID="closeAndContinueButton"
            onPress={handleClose} 
            style={styles.actionButton}
          >
            <LinearGradient
              locations={[0, 0.7, 1]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              colors={['#ffb636', '#ffb636', '#d47d02']}
              style={styles.buttonGradient}
            >
              <Text style={styles.buttonText}>
                {I18n.t('cartAssignment.issueDetails.closeButton')}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    paddingHorizontal: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    maxHeight: '85%',
    borderRadius: 8,
    borderWidth: 3,
    borderColor: '#e8971a',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 15,
    paddingBottom: 10,
  },
  title: {
    fontSize: 22,
    fontFamily: 'Poppins_300Light',
    fontWeight: '600',
    color: '#1C1C1C',
    flex: 1,
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: 15,
    top: 10,
    padding: 5,
  },
  closeIcon: {
    fontSize: 24,
    color: '#1C1C1C',
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  successIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 15,
    marginHorizontal: 'auto'
  },
  checkIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  checkMark: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  noIssuesText: {
    fontSize: 16,
    fontFamily: 'Poppins_300Light',
    color: '#1C1C1C',
    fontWeight: '500',
    flex: 1,
  },
  issueSection: {
    marginBottom: 20,
  },
  issueTitle: {
    fontSize: 16,
    fontFamily: 'Poppins_300Light',
    fontWeight: '600',
    color: '#1C1C1C',
    marginBottom: 10,
  },
  issueScrollView: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 15,
    maxHeight: 120,
  },
  issueText: {
    fontSize: 14,
    fontFamily: 'Poppins_300Light',
    color: '#1C1C1C',
    lineHeight: 20,
    marginBottom: 8,
  },
  successSection: {
    flex: 1,
  },
  successTitle: {
    fontSize: 16,
    fontFamily: 'Poppins_300Light',
    fontWeight: '600',
    color: '#1C1C1C',
    textAlign: 'center',
    marginBottom: 15,
  },
  successScrollView: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 15,
    flex: 1,
  },
  successText: {
    fontSize: 14,
    fontFamily: 'Poppins_300Light',
    color: '#1C1C1C',
    lineHeight: 20,
    marginBottom: 6,
  },
  footerContainer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 10,
  },
  actionButton: {
    width: '100%',
  },
  buttonGradient: {
    borderWidth: 1,
    borderColor: '#1C1C1C',
    borderRadius: 25,
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default CartAssignmentDetails;
